import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { detectCountryDomain } from '../lib/country-domain';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'nl', label: 'Nederlands', short: 'NL' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'pt-BR', label: 'Português', short: 'PT' },
  { code: 'es', label: 'Español', short: 'ES' },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

// Seed the UI language from the country domain (.pt→pt-BR, .be/.nl→nl, .fr→fr)
// ONLY on a first visit. An explicit stored choice always wins.
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'countryDomain',
  lookup() {
    // Respect an explicit prior choice; only seed the default on first visit.
    try {
      if (localStorage.getItem('cb_market_lng2')) return undefined;
    } catch {
      /* localStorage unavailable → fall through to domain default */
    }
    const { code, defaultLang } = detectCountryDomain();
    return code ? defaultLang : undefined;
  },
  cacheUserLanguage() {
    /* no-op: caching handled by the localStorage detector */
  },
});

// M7: only English (the fallback) is bundled. The other catalogs are their own
// chunks, fetched by this backend for the active language only. i18next waits
// for the catalog before it switches language (init and changeLanguage both
// resolve after the load), so a switch never flashes raw keys. All Portuguese
// variants share the pt-BR catalog.
type Catalog = { default: ResourceKey };
const LAZY_CATALOGS: Record<string, () => Promise<Catalog>> = {
  nl: () => import(/* webpackChunkName: "locale-nl" */ './locales/nl.json'),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es.json'),
  pt: () => import(/* webpackChunkName: "locale-pt-BR" */ './locales/pt-BR.json'),
  'pt-BR': () => import(/* webpackChunkName: "locale-pt-BR" */ './locales/pt-BR.json'),
  'pt-PT': () => import(/* webpackChunkName: "locale-pt-BR" */ './locales/pt-BR.json'),
};

const lazyCatalogs: BackendModule = {
  type: 'backend',
  init() {
    /* nothing to configure */
  },
  read(language, _namespace, callback) {
    const load = LAZY_CATALOGS[language];
    if (!load) {
      // 'en' is bundled; anything else unsupported falls back to it.
      callback(null, language === 'en' ? (en as ResourceKey) : {});
      return;
    }
    load().then(
      (m) => callback(null, m.default),
      (err: Error) => callback(err, false),
    );
  },
};

/** Resolves once the detected language's catalog is in (main.tsx waits for it before the first render). */
export const i18nReady = i18n
  .use(lazyCatalogs)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    // Bundled `en` + backend-loaded others.
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: ['en', 'nl', 'fr', 'pt', 'pt-BR', 'pt-PT', 'es'],
    load: 'currentOnly',
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      // No 'htmlTag' detector: index.html hardcodes lang="en", and i18next
      // prefers any exact supported match ('en') over mapping regional
      // locales (nl-BE/fr-BE/…) to their base language — so htmlTag forced
      // English on every regional-locale visitor. Storage key is bumped to
      // _lng2 because the old key holds the 'en' that bug cached.
      order: ['localStorage', 'countryDomain', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'cb_market_lng2',
    },
  });

// Full BCP-47 tag: our Portuguese is Brazilian, so the document must say
// pt-BR, not a bare "pt" that reads as European Portuguese (SEO audit INT-09).
// The server-rendered shell sets the same tag; this keeps the two in step.
const applyHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};
applyHtmlLang(i18n.resolvedLanguage || i18n.language || 'en');
i18n.on('languageChanged', applyHtmlLang);

export default i18n;
